import { useMutation } from '@apollo/client/react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { PageDrawer } from 'common/components/PageDrawer';
import { IPageDrawerProps } from 'common/components/PageDrawer/PageDrawer';
import {
    ICreateModuleGroupInput,
    IModuleGroupConditionType,
    CreateModuleGroupDocument,
    TrainingModuleGroupsDocument,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { TModuleGroupFormValues } from 'training/types';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { ModuleGroupForm } from 'training/components/forms/ModuleGroupForm';
import { PageTitle } from 'common/components/PageTitle';
import { getConditionFormValues } from 'training/utils/moduleGroup';

interface IProps extends IPageDrawerProps {
    onClose?(): void;
}

export const ModuleGroupCreateDrawer = ({
    open,
    onClose,
    ...other
}: IProps) => {
    const { trainingId } = useParams() as { trainingId: string };
    const { showApolloError } = useApolloError();
    const [displaySnackbar] = useSnackbar();
    const [translate] = useTranslation();
    const [createModuleGroup, { loading: createModuleGroupLoading }] =
        useMutation(CreateModuleGroupDocument);

    const handleSubmit = async (values: TModuleGroupFormValues) => {
        const { conditionType, absoluteCondition, ...otherValues } = values;

        const conditionValues = conditionType
            ? getConditionFormValues(conditionType, values)
            : {};

        try {
            await createModuleGroup({
                variables: {
                    trainingId,
                    moduleGroup: {
                        conditionType,
                        ...otherValues,
                        ...conditionValues,
                    } as ICreateModuleGroupInput,
                },
                refetchQueries: [
                    {
                        query: TrainingModuleGroupsDocument,
                        variables: { id: trainingId },
                    },
                ],
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(translate('moduleGroupForm.createSuccess'), {
            variant: 'success',
        });

        onClose?.();

        return;
    };

    const initialValues: TModuleGroupFormValues = {
        title: '',
        description: '',
        conditionType: IModuleGroupConditionType.NoCondition,
    };

    return (
        <PageDrawer
            disableClose={createModuleGroupLoading}
            open={open}
            onClose={onClose}
            {...other}
        >
            {open && (
                <>
                    <PageTitle>
                        {translate('moduleGroupForm.pageTitleCreate')}
                    </PageTitle>

                    <ModuleGroupForm
                        initialValues={initialValues}
                        loading={createModuleGroupLoading}
                        onClose={onClose}
                        onSubmit={handleSubmit}
                    />
                </>
            )}
        </PageDrawer>
    );
};
